import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { readHumanErrorMessage } from "@/shared/utils/apiError";

import { CardholderOnboardingService } from "../services/cardholderOnboarding.service";
import type { CardholderOnboardingRun } from "../types/cardholderOnboarding.types";

export type CardholderSendPhase = "checking" | "review" | "sending" | "done" | "error";

/**
 * Sends cardholders to the card provider in two calls: a dry run of exactly these identities, then the real
 * run of only the ones the dry run accepted, so nothing is queued that the review did not show.
 */
export function useCardholderSend(organizationId: string, identityIds: string[], fallbackError: string) {
  const [phase, setPhase] = useState<CardholderSendPhase>("checking");
  const [preview, setPreview] = useState<CardholderOnboardingRun | null>(null);
  const [result, setResult] = useState<CardholderOnboardingRun | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const checkedRef = useRef(false);

  const requestPreview = useCallback(async () => {
    // Without `identity_ids` the run scans the whole organization, so an empty pick is never sent.
    if (identityIds.length === 0) throw new Error(fallbackError);
    return CardholderOnboardingService.runOnboarding({
      organization_id: organizationId,
      identity_ids: identityIds,
      dry_run: true,
    });
  }, [organizationId, identityIds, fallbackError]);

  const showPreview = useCallback((run: CardholderOnboardingRun) => {
    setPreview(run);
    setPhase("review");
  }, []);

  const showFailure = useCallback(
    (err: unknown) => {
      setErrorMessage(readHumanErrorMessage(err, fallbackError));
      setPhase("error");
    },
    [fallbackError],
  );

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    requestPreview().then(showPreview, showFailure);
  }, [requestPreview, showPreview, showFailure]);

  const check = useCallback(async () => {
    setPhase("checking");
    setErrorMessage(null);
    try {
      showPreview(await requestPreview());
    } catch (err) {
      showFailure(err);
    }
  }, [requestPreview, showPreview, showFailure]);

  const accepted = useMemo(
    () => preview?.results.filter((row) => row.outcome !== "skipped").map((row) => row.identity_id) ?? [],
    [preview],
  );

  const send = useCallback(async (): Promise<boolean> => {
    if (accepted.length === 0) return false;
    setPhase("sending");
    setErrorMessage(null);
    try {
      const run = await CardholderOnboardingService.runOnboarding({
        organization_id: organizationId,
        identity_ids: accepted,
        dry_run: false,
      });
      setResult(run);
      setPhase("done");
      return true;
    } catch (err) {
      setErrorMessage(readHumanErrorMessage(err, fallbackError));
      setPhase("error");
      return false;
    }
  }, [accepted, organizationId, fallbackError]);

  return { phase, preview, result, accepted, errorMessage, check, send };
}
